import {
  Plug,
  KeyRound,
  Boxes,
  ShoppingCart,
  Webhook,
  Undo2,
  FlaskConical,
  Boxes as BoxesIcon,
  BookMarked,
  ShoppingBag,
  Server,
  ShieldCheck,
  ToggleRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Lead,
  P,
  H3,
  UL,
  LI,
  Code,
  Callout,
  Steps,
  Step,
  FlowNode,
  FlowArrow,
  Lifecycle,
  DefList,
  Matrix,
  Checklist,
  CodeBlock,
  Endpoints,
} from './components';
import type { GuideChapter } from './tracks';

function GoTo({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-1.5 py-0.5 text-[12px] font-medium text-primary ring-1 ring-inset ring-primary/20 hover:bg-primary/15"
    >
      {children}
    </Link>
  );
}

const BASE = 'https://zafroi.com/api/v1';

// ── 1 · Overview ───────────────────────────────────────────────────────────
function Overview() {
  return (
    <div>
      <Lead>
        Zafroi runs your business as a <strong className="text-foreground">headless backend</strong>:
        your own website or app (the “portal”) shows the storefront, while Zafroi holds the catalogue,
        prices, stock, orders, invoices and delivery. The two talk over a REST API — your portal{' '}
        <em>reads</em> the catalogue and <em>creates</em> orders; Zafroi <em>pushes</em> events back as
        things happen.
      </Lead>

      <H3>How the pieces connect</H3>
      <div className="my-5 space-y-3 rounded-2xl border border-border/60 bg-muted/20 p-4">
        <div className="flex flex-wrap items-center gap-1.5">
          <FlowNode icon={ShoppingBag} title="Your storefront" sub="website / app" tone="accent" />
          <FlowArrow />
          <FlowNode icon={Plug} title="Zafroi API" sub="/integrations/*" />
          <FlowArrow />
          <FlowNode icon={Server} title="Catalogue · orders" sub="your tenant data" tone="success" />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <FlowNode icon={Webhook} title="Zafroi events" sub="order.*, payment.*…" tone="warn" />
          <FlowArrow />
          <FlowNode icon={Server} title="Your endpoint" sub="webhook receiver" tone="accent" />
        </div>
      </div>
      <P>
        Everything you need is under the <strong>Developer</strong> section of the left nav:{' '}
        <GoTo to="/platform/api-reference">Reference</GoTo>,{' '}
        <GoTo to="/platform/api-clients">Integrations</GoTo>,{' '}
        <GoTo to="/platform/api-keys">Keys</GoTo>,{' '}
        <GoTo to="/platform/webhooks">Webhooks</GoTo>,{' '}
        <GoTo to="/platform/portal-preview">Portal Preview</GoTo> and the{' '}
        <GoTo to="/platform/api-activity">API Logs</GoTo>.
      </P>

      <H3>The five steps to go live</H3>
      <Steps>
        <Step title="Register your portal as an Integration">
          One record for your external system (its name + purpose) under{' '}
          <GoTo to="/platform/api-clients">Integrations</GoTo>.
        </Step>
        <Step title="Issue an API key & store the secret">
          A credential for that integration — shown once. Keep it server-side.
        </Step>
        <Step title="Read the catalogue & create orders">
          Call <Code>/integrations/*</Code> with your key.
        </Step>
        <Step title="Subscribe to webhooks & verify signatures">
          So your portal reacts when payments succeed, shipments move, returns open.
        </Step>
        <Step title="Test in Portal Preview, then ship">
          Walk the whole journey against real APIs before you point production traffic at it.
        </Step>
      </Steps>

      <Callout tone="info" title="The Reference page is the source of truth">
        This guide shows the shape of each call. The in-app{' '}
        <GoTo to="/platform/api-reference">Reference</GoTo> page lists every endpoint with its exact
        request/response schema and live examples — always check it for field-level detail.
      </Callout>
    </div>
  );
}

// ── 2 · Authentication & keys ────────────────────────────────────────────────
function Keys() {
  return (
    <div>
      <Lead>
        Every API call is authenticated with a bearer <strong className="text-foreground">API key</strong>.
        A key belongs to an <strong className="text-foreground">Integration</strong> (your registered
        external system), and it’s scoped to your tenant — it can only ever see your own data.
      </Lead>

      <H3>Integration vs. key</H3>
      <DefList
        items={[
          { term: 'Integration', def: <>The external system itself — e.g. “Portal — production”. Created under <GoTo to="/platform/api-clients">Integrations</GoTo> with a name + purpose (Orders, Inventory, Webhooks…). It groups keys and webhook deliveries.</> },
          { term: 'API key', def: <>A credential issued under an integration. You can hold several (e.g. test + live, or for rotation). Created under <GoTo to="/platform/api-keys">Keys</GoTo>.</> },
        ]}
      />

      <H3>Issue a key</H3>
      <Steps>
        <Step title="Create the integration">
          <GoTo to="/platform/api-clients">Integrations</GoTo> → name it and pick a purpose.
        </Step>
        <Step title="Issue a key under it">
          <GoTo to="/platform/api-keys">Keys</GoTo> → choose the integration, label the key. Use a{' '}
          <Code>zfk_test_</Code> key while building and a <Code>zfk_live_</Code> key for production.
        </Step>
        <Step title="Copy the secret now — it’s shown once">
          The full key is revealed a single time at creation. Store it in your server’s secret manager
          immediately; afterwards only a masked version is shown.
        </Step>
      </Steps>

      <H3>Authenticate a request</H3>
      <P>Send the key as a bearer token on every call:</P>
      <CodeBlock language="bash">{`curl ${BASE}/integrations/inventory/items \\
  -H "Authorization: Bearer zfk_live_xxxxxxxxxxxxxxxx"`}</CodeBlock>

      <Callout tone="warn" title="Never expose a key in the browser">
        API keys are <strong>server-side</strong> secrets. Call Zafroi from your portal’s backend, not
        from client-side JavaScript — a key in the browser can be copied and abused. Rotate a key (issue
        new, revoke old) the moment one might be compromised.
      </Callout>
    </div>
  );
}

// ── 3 · Browse the catalogue ─────────────────────────────────────────────────
function Catalogue() {
  return (
    <div>
      <Lead>
        Your storefront pages are built from read-only catalogue endpoints. They return items, their
        variants, categories and live stock state — optionally priced in any currency.
      </Lead>

      <Endpoints
        items={[
          { method: 'GET', path: '/integrations/inventory/items', desc: 'List items (paginated, filterable)' },
          { method: 'GET', path: '/integrations/inventory/items/:sku', desc: 'One item + its variants' },
          { method: 'GET', path: '/integrations/inventory/items/:sku/variants', desc: 'Resolve a variant by attributes' },
          { method: 'GET', path: '/integrations/inventory/categories', desc: 'Category tree' },
          { method: 'GET', path: '/integrations/inventory/availability', desc: 'Stock state per SKU' },
          { method: 'GET', path: '/integrations/currency/convert', desc: 'Convert a price for display' },
        ]}
      />

      <H3>Listing items</H3>
      <P>
        Filter by <Code>categoryId</Code>, <Code>search</Code>, a bulk <Code>sku</Code> list or{' '}
        <Code>updatedAfter</Code> (to refresh a cache), and page with <Code>page</Code> +{' '}
        <Code>pageSize</Code>:
      </P>
      <CodeBlock language="bash">{`curl "${BASE}/integrations/inventory/items?categoryId=tees&page=1&pageSize=24&currency=USD" \\
  -H "Authorization: Bearer zfk_live_…"`}</CodeBlock>

      <H3>Resolving a variant</H3>
      <P>
        Turn a shopper’s selected options into a concrete SKU by passing the attributes as query
        params:
      </P>
      <CodeBlock language="bash">{`curl "${BASE}/integrations/inventory/items/TSHIRT/variants?Size=M&Color=White" \\
  -H "Authorization: Bearer zfk_live_…"`}</CodeBlock>

      <H3>Stock & currency</H3>
      <UL>
        <LI><strong>Availability</strong> returns a clear state per SKU — <Code>IN_STOCK</Code>, <Code>LOW</Code> or <Code>OUT_OF_STOCK</Code> — so you can badge products without exposing exact counts.</LI>
        <LI><strong>Currency convert</strong> is <em>display-only</em>: it prices the catalogue in the shopper’s currency using live ECB rates, but the order is still placed and invoiced in your base currency.</LI>
      </UL>

      <Callout tone="tip" title="Cache, then invalidate on webhook">
        Cache catalogue responses for speed, and subscribe to the <Code>inventory.*</Code> webhooks
        (item updated, price changed, stock changed) to refresh exactly when something changes — see
        the Webhooks chapter.
      </Callout>
    </div>
  );
}

// ── 4 · Checkout & orders ────────────────────────────────────────────────────
function Checkout() {
  return (
    <div>
      <Lead>
        Checkout is a short sequence: reserve the stock, identify the customer, quote delivery, create
        the order, then take payment. Each step is its own endpoint so you can build the UX your way.
      </Lead>

      <div className="my-5 space-y-3 rounded-2xl border border-border/60 bg-muted/20 p-4">
        <div className="flex flex-wrap items-center gap-1.5">
          <FlowNode icon={ShoppingCart} title="Hold stock" sub="checkout/hold" tone="accent" />
          <FlowArrow />
          <FlowNode icon={Boxes} title="Upsert customer" sub="by externalRef" />
          <FlowArrow />
          <FlowNode icon={Plug} title="Delivery options" sub="Standard / Express" />
          <FlowArrow />
          <FlowNode icon={ShoppingBag} title="Create order" sub="externalOrderRef" tone="accent" />
          <FlowArrow />
          <FlowNode icon={ShieldCheck} title="Take payment" sub="initiate → poll" tone="success" />
        </div>
      </div>

      <Endpoints
        items={[
          { method: 'POST', path: '/integrations/checkout/hold', desc: 'Reserve stock → holdId + expiresAt' },
          { method: 'GET', path: '/integrations/checkout/hold/:holdId', desc: 'Hold status + seconds left' },
          { method: 'DELETE', path: '/integrations/checkout/hold/:holdId', desc: 'Release a hold' },
          { method: 'POST', path: '/integrations/customers', desc: 'Upsert customer (idempotent)' },
          { method: 'GET', path: '/integrations/delivery-options', desc: 'Tiers + charge + EDD + quoteId' },
          { method: 'POST', path: '/integrations/orders', desc: 'Create order (409 if out of stock)' },
          { method: 'GET', path: '/integrations/orders/:externalOrderRef', desc: 'Fetch an order' },
          { method: 'POST', path: '/integrations/payments/initiate', desc: 'Start a payment' },
          { method: 'GET', path: '/integrations/payments/:externalPaymentRef', desc: 'Poll payment status' },
        ]}
      />

      <H3>Quote delivery</H3>
      <P>
        Ask for delivery options with the destination and parcel details — you get back the tiers you’ve
        configured (e.g. Standard / Express) each with a price, an expected delivery date and a{' '}
        <Code>quoteId</Code> to carry into the order:
      </P>
      <CodeBlock language="bash">{`curl "${BASE}/integrations/delivery-options?deliveryPincode=400001&weightGrams=500&subtotal=1499&currency=INR" \\
  -H "Authorization: Bearer zfk_live_…"`}</CodeBlock>

      <H3>Create the order</H3>
      <P>
        Send your own <Code>externalOrderRef</Code> (your cart/checkout id), the customer, the line
        items and the chosen delivery option. The shape below is illustrative — the{' '}
        <GoTo to="/platform/api-reference">Reference</GoTo> page has the exact schema:
      </P>
      <CodeBlock language="http">{`POST ${BASE}/integrations/orders
Authorization: Bearer zfk_live_…
Content-Type: application/json

{
  "externalOrderRef": "WEB-10231",
  "customer": { "externalRef": "CUST-55" },
  "items": [
    { "sku": "TSHIRT-WHT-M", "quantity": 2 }
  ],
  "deliveryOptionId": "<optionId from delivery-options>"
}`}</CodeBlock>

      <Callout tone="warn" title="Idempotency & the 409">
        Order and customer creates are <strong>idempotent</strong> on your reference — retrying with the
        same <Code>externalOrderRef</Code> returns the existing order, so a network retry never
        double-creates. If stock ran out between hold and create, you get a <Code>409 Conflict</Code> —
        handle it by re-quoting or offering an alternative.
      </Callout>
    </div>
  );
}

// ── 5 · Webhooks ─────────────────────────────────────────────────────────────
function Webhooks() {
  return (
    <div>
      <Lead>
        Webhooks let your portal react the moment something happens in Zafroi — a payment succeeds, a
        shipment moves, a return opens — instead of polling. You register a URL under{' '}
        <GoTo to="/platform/webhooks">Webhooks</GoTo> and choose which events to receive.
      </Lead>

      <H3>Events you can subscribe to</H3>
      <DefList
        items={[
          { term: 'Sale cycle', def: <><Code>order.created</Code>, <Code>order.status.changed</Code>, <Code>payment.initiated/succeeded/failed</Code>, <Code>dispatch.created</Code>, <Code>dispatch.status.changed</Code>, <Code>tracking.event.recorded</Code></> },
          { term: 'After-sales', def: <><Code>return.created</Code>, <Code>return.status.changed</Code>, <Code>refund.initiated/succeeded/failed</Code></> },
          { term: 'Cancellations', def: <><Code>cancel.requested</Code>, <Code>cancel.approved/rejected</Code>, <Code>cancel.refund_initiated/confirmed/failed</Code></> },
          { term: 'Catalogue', def: <><Code>inventory.item.updated</Code>, <Code>inventory.item.price.changed</Code>, <Code>inventory.stock.changed</Code> — to invalidate your cache</> },
        ]}
      />

      <H3>Verifying a delivery</H3>
      <P>Every webhook carries three headers:</P>
      <UL>
        <LI><Code>X-Zafroi-Event</Code> — the event type, e.g. <Code>order.created</Code>.</LI>
        <LI><Code>X-Zafroi-Delivery</Code> — a stable delivery id; use it as your idempotency key (retries reuse it).</LI>
        <LI><Code>X-Zafroi-Signature</Code> — an HMAC signature, format <Code>t=&lt;unix&gt;,v1=&lt;hex&gt;</Code>.</LI>
      </UL>
      <P>
        Verify the signature against your endpoint’s secret (shown once when you create the webhook)
        before trusting the payload. It’s HMAC-SHA256 over <Code>{'`${t}.${rawBody}`'}</Code>, with a
        5-minute replay window:
      </P>
      <CodeBlock language="javascript">{`import crypto from 'node:crypto';

function verifyZafroiSignature(rawBody, header, secret) {
  const parts = Object.fromEntries(
    header.split(',').map((p) => p.split('=').map((s) => s.trim())),
  );
  const { t, v1 } = parts;
  if (!t || !v1) return false;
  // Replay protection — reject if older than 5 minutes
  if (Math.abs(Math.floor(Date.now() / 1000) - Number(t)) > 300) return false;
  const expected = crypto
    .createHmac('sha256', secret)
    .update(\`\${t}.\${rawBody}\`)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(expected, 'hex'),
    Buffer.from(v1, 'hex'),
  );
}`}</CodeBlock>

      <Callout tone="tip" title="Reply 2xx fast, process async">
        Acknowledge with a <Code>200</Code> quickly and do the work in the background. Zafroi retries on
        non-2xx, reusing the same <Code>X-Zafroi-Delivery</Code> id — so persist that id and skip
        duplicates.
      </Callout>

      <H3>Inbound webhooks (carriers → Zafroi)</H3>
      <P>
        The other direction is handled for you: when you connect a carrier, its tracking updates flow
        into Zafroi automatically and surface to you as <Code>tracking.event.recorded</Code> /{' '}
        <Code>dispatch.status.changed</Code>. You don’t build those receivers.
      </P>
    </div>
  );
}

// ── 6 · After-sales ──────────────────────────────────────────────────────────
function AfterSales() {
  return (
    <div>
      <Lead>
        Cancellations, returns and refunds run as a request → review → settlement loop. Your portal
        raises the request; an operator decides in the ERP; your portal is told the outcome (by webhook
        and/or a confirmation callback).
      </Lead>

      <Endpoints
        items={[
          { method: 'POST', path: '/integrations/cancels', desc: 'Customer cancel request → PENDING_REVIEW' },
          { method: 'POST', path: '/integrations/cancels/:externalRef/refund-confirmed', desc: 'Portal confirms refund outcome' },
          { method: 'POST', path: '/integrations/returns', desc: 'Create a return (idempotent)' },
          { method: 'GET', path: '/integrations/returns/:externalOrderRef', desc: 'List returns for an order' },
          { method: 'GET', path: '/tracking/:trackingNumber', desc: 'Public shipment tracking' },
        ]}
      />

      <H3>The cancel → refund loop</H3>
      <Lifecycle
        states={[
          { label: 'cancel.requested', tone: 'warn' },
          { label: 'PENDING_REVIEW', tone: 'default' },
          { label: 'approved', tone: 'accent' },
          { label: 'refund_initiated', tone: 'default' },
          { label: 'refund_confirmed', tone: 'success' },
        ]}
        branches={[{ from: 'review', to: [{ label: 'rejected', tone: 'danger' }] }]}
      />
      <UL>
        <LI>Portal <Code>POST /integrations/cancels</Code> → it lands as <Code>PENDING_REVIEW</Code> in the ERP.</LI>
        <LI>An operator approves/rejects. On approval Zafroi creates a refund and fires <Code>cancel.refund_initiated</Code>.</LI>
        <LI>If your portal owns the gateway, it processes the refund and calls <Code>…/refund-confirmed</Code> with <Code>SUCCESS</Code> or <Code>FAILED</Code> to close the loop.</LI>
      </UL>

      <Callout tone="info" title="You don’t reconcile by hand">
        Each transition emits a webhook (<Code>cancel.*</Code>, <Code>return.*</Code>,{' '}
        <Code>refund.*</Code>), so your portal’s order page can mirror the ERP’s state in real time.
      </Callout>
    </div>
  );
}

// ── 7 · Test & debug ─────────────────────────────────────────────────────────
function Testing() {
  return (
    <div>
      <Lead>
        Three tools let you build and debug an integration without touching production data or guessing
        at payloads.
      </Lead>

      <H3>Portal Preview — walk the journey</H3>
      <P>
        <GoTo to="/platform/portal-preview">Portal Preview</GoTo> mints a temporary 15-minute
        portal-scoped key and steps you through Browse → Order → Pay → Confirm against the{' '}
        <em>real</em> <Code>/integrations/*</Code> endpoints, with a live tracer that shows the exact
        request and response for each call — copy those straight into your portal code. An after-sales
        panel exercises cancel + return too.
      </P>

      <H3>API Logs — see every call</H3>
      <P>
        <GoTo to="/platform/api-activity">API Logs</GoTo> shows inbound calls your portal makes into
        Zafroi (method, path, status, response time, and a <strong>request id</strong> to trace), plus
        outbound calls Zafroi makes to carriers and gateways.
      </P>

      <H3>Business Errors — why a call was refused</H3>
      <P>
        <GoTo to="/platform/business-errors">Business Errors</GoTo> logs every rejected action —{' '}
        <Code>400</Code> bad input, <Code>409</Code> conflict, <Code>422</Code> validation — with the
        exact reason, the route, the actor (Portal vs Operator) and the request id. It’s the fastest way
        to find why an order create bounced.
      </P>

      <H3>Saved Views — your own read endpoints</H3>
      <P>
        Need a list the standard endpoints don’t give you? <GoTo to="/platform/saved-views">Saved Views</GoTo>{' '}
        lets you define a filtered, projected read — no code — exposed at a stable{' '}
        <Code>GET /integrations/views/&#123;slug&#125;</Code> your portal can call like any other endpoint.
      </P>

      <Callout tone="tip" title="Trace by request id">
        Every response carries a request id. When something misbehaves, search that id in API Logs (and
        Business Errors) to see exactly what Zafroi received and why it answered the way it did.
      </Callout>
    </div>
  );
}

// ── 8 · No-code connectors ───────────────────────────────────────────────────
function Connectors() {
  return (
    <div>
      <Lead>
        Some integrations don’t need any code at all. <strong className="text-foreground">Connectors</strong>{' '}
        are ready-made links to common partners — you just enter the partner’s credentials and turn them
        on. Use a connector where one exists; use the API for anything custom.
      </Lead>

      <H3>What’s available</H3>
      <DefList
        items={[
          { term: 'Shipping', def: <><strong>Shiprocket</strong> (live) — 20+ couriers, auto-fulfil + tracking. Delhivery, BlueDart coming.</> },
          { term: 'Payments', def: <><strong>Razorpay</strong> (live) — UPI, cards, netbanking, wallets; capture + refund. Cashfree, Stripe coming.</> },
          { term: 'Accounting', def: <><strong>Tally</strong> (live) — export invoices, payments, credit notes. Zoho Books coming.</> },
          { term: 'Communication', def: <><strong>WhatsApp Business</strong> (live) — order, dispatch & tracking updates.</> },
          { term: 'Tax', def: <><strong>GST e-invoicing</strong> (preview) — IRN + QR for B2B invoices.</> },
          { term: 'Marketplaces / storefronts', def: <>Amazon, Flipkart, Shopify — coming soon.</> },
        ]}
      />

      <P>
        Configure them under <GoTo to="/platform/connectors">Connectors</GoTo>: enter the partner’s
        credentials (stored encrypted in the credential vault, never shown again) and hit{' '}
        <strong>Test</strong> to verify before going live.
      </P>

      <H3>Connector or custom API?</H3>
      <Matrix
        columns={['Connector', 'Custom API']}
        rows={[
          { label: 'A partner connector exists', cells: [true, false] },
          { label: 'No code to write', cells: [true, false] },
          { label: 'Your own storefront / bespoke flow', cells: [false, true] },
          { label: 'Full control over payloads & timing', cells: [false, true] },
        ]}
      />
    </div>
  );
}

// ── 9 · Reference & go-live ──────────────────────────────────────────────────
function Reference() {
  return (
    <div>
      <Lead>Everything you need on one page to take an integration to production.</Lead>

      <H3>The essentials</H3>
      <DefList
        items={[
          { term: 'Base URL', def: <Code>{BASE}</Code> },
          { term: 'Auth header', def: <Code>Authorization: Bearer zfk_live_…</Code> },
          { term: 'Key prefixes', def: <><Code>zfk_test_</Code> while building, <Code>zfk_live_</Code> in production</> },
          { term: 'Webhook headers', def: <><Code>X-Zafroi-Event</Code>, <Code>X-Zafroi-Delivery</Code>, <Code>X-Zafroi-Signature</Code></> },
          { term: 'Signature', def: <>HMAC-SHA256 of <Code>{'`${t}.${rawBody}`'}</Code>, 5-minute replay window</> },
          { term: 'Idempotency', def: <>Your <Code>externalOrderRef</Code> / <Code>externalRef</Code> on creates; <Code>X-Zafroi-Delivery</Code> on webhooks</> },
        ]}
      />

      <H3>Go-live checklist</H3>
      <Checklist
        items={[
          { label: 'Integration registered for production', to: '/platform/api-clients' },
          { label: 'Live key issued; secret stored server-side', to: '/platform/api-keys' },
          { label: 'Catalogue + checkout calls working against live key', to: '/platform/api-reference' },
          { label: 'Webhook endpoint subscribed; signature verified', to: '/platform/webhooks' },
          { label: 'Idempotency + 409 handling in place', to: '/platform/business-errors' },
          { label: 'Full journey passes in Portal Preview', to: '/platform/portal-preview' },
        ]}
      />

      <H3>Common pitfalls</H3>
      <UL>
        <LI><strong>Key in client-side code</strong> — always call from your backend; never ship a key to the browser.</LI>
        <LI><strong>Skipping signature verification</strong> — an unverified webhook can be spoofed. Verify before acting.</LI>
        <LI><strong>No idempotency</strong> — retries then double-create orders. Always send a stable reference.</LI>
        <LI><strong>Ignoring the 409</strong> — stock can sell out between hold and create; handle it gracefully.</LI>
        <LI><strong>Polling instead of webhooks</strong> — subscribe to events; it’s faster and lighter.</LI>
      </UL>

      <Callout tone="success" title="That’s portal integration">
        Keys → catalogue → checkout → webhooks → after-sales, tested in Portal Preview. Pair this with{' '}
        <Link to="/guide/procurement" className="font-medium text-primary hover:underline">Procurement</Link>{' '}
        (materials in) and the upcoming Sales track (orders out) for the full loop.
      </Callout>
    </div>
  );
}

// ── Chapters export ──────────────────────────────────────────────────────────
export const portalChapters: GuideChapter[] = [
  { id: 'overview', title: 'Overview', icon: Plug, render: Overview },
  { id: 'keys', title: 'Authentication & keys', icon: KeyRound, render: Keys },
  { id: 'catalogue', title: 'Browse the catalogue', icon: BoxesIcon, render: Catalogue },
  { id: 'checkout', title: 'Checkout & orders', icon: ShoppingCart, render: Checkout },
  { id: 'webhooks', title: 'Webhooks', icon: Webhook, render: Webhooks },
  { id: 'after-sales', title: 'After-sales', icon: Undo2, render: AfterSales },
  { id: 'testing', title: 'Test & debug', icon: FlaskConical, render: Testing },
  { id: 'connectors', title: 'No-code connectors', icon: ToggleRight, render: Connectors },
  { id: 'reference', title: 'Reference & go-live', icon: BookMarked, render: Reference },
];
